import { Button, cn } from '@heroui/react';
import { EmojiLookDown } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

type ErrorFallbackProps = {
  title?: string;
  description?: string;
  onReload?: () => void;
  onGoBack?: () => void;
  className?: string;
};

/**
 * ErrorFallback — friendly full-page state shown when a feature crashes.
 *
 * @example
 * <ErrorFallback />
 */
export function ErrorFallback({
  title,
  description,
  onReload,
  onGoBack,
  className
}: ErrorFallbackProps) {
  const { t } = useTranslation();

  const handleReload = onReload ?? (() => window.location.reload());
  const handleGoBack = onGoBack ?? (() => window.history.back());

  return (
    <div
      role='alert'
      className={cn(
        'flex size-full min-h-[80vh] flex-col items-center justify-center gap-6 p-10 text-center',
        className
      )}
    >
      <div className='flex h-20 w-20 items-center justify-center rounded-2xl bg-surface-dim'>
        <EmojiLookDown className='h-12 w-12 text-on-surface' aria-hidden />
      </div>

      <div className='flex max-w-md flex-col gap-2'>
        <h1 className='text-headline-md font-semibold text-on-surface'>
          {title ?? t('label.an_error_happened_title_empty')}
        </h1>
        <p className='text-body-regular-md text-on-surface-variant'>
          {description ?? t('label.try_reload_page_or_support')}
        </p>
      </div>

      <div className='flex flex-wrap items-center justify-center gap-3'>
        <Button color='primary' onPress={handleReload}>
          {t('action.reload_page')}
        </Button>
        <Button variant='bordered' onPress={handleGoBack}>
          {t('action.back')}
        </Button>
      </div>
    </div>
  );
}
